import React, { useState } from 'react';
import { Typography, Card, Upload, Button, message } from 'antd';
import { InboxOutlined, FileExcelOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import './EtsyAccounting.css';

const { Title, Text } = Typography;
const { Dragger } = Upload;

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function EtsyAccounting() {
  const [file, setFile] = useState(null);
  const [converting, setConverting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleBeforeUpload = (f) => {
    const isCSV = f.type === 'text/csv' || f.name.toLowerCase().endsWith('.csv');
    if (!isCSV) {
      message.error('Please upload a CSV file');
      return Upload.LIST_IGNORE;
    }
    if (f.size > 20 * 1024 * 1024) {
      message.error('File must be smaller than 20 MB');
      return Upload.LIST_IGNORE;
    }
    setFile(f);
    setSuccess(false);
    return false;
  };

  const handleClear = () => {
    setFile(null);
    setSuccess(false);
  };

  const handleConvert = async () => {
    if (!file) return;
    setConverting(true);
    setSuccess(false);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post('/etsy-accounting/convert', formData, {
        responseType: 'blob',
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const disposition = response.headers['content-disposition'];
      let filename = file.name.replace(/\.csv$/i, '') + '.xlsx';
      if (disposition) {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(true);
      message.success('Excel file downloaded successfully');
    } catch (err) {
      const msg = err.response?.data?.error || 'Conversion failed. Please try again.';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          message.error(parsed.error || msg);
        } catch {
          message.error(msg);
        }
      } else {
        message.error(msg);
      }
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="etsy-accounting-page">
      <Title level={3} className="page-title">Etsy Accounting</Title>
      <Text className="page-subtitle">
        Upload an Etsy CSV statement to convert it into an accounting Excel file with Net Amount, Expenses, and Total Amount computed per order.
      </Text>

      <Card className="etsy-accounting-upload-card">
        {!file ? (
          <div className="etsy-accounting-dragger">
            <Dragger
              accept=".csv"
              multiple={false}
              showUploadList={false}
              beforeUpload={handleBeforeUpload}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag your Etsy CSV statement here</p>
              <p className="ant-upload-hint">Supports Etsy monthly statement CSV files (INR)</p>
            </Dragger>
          </div>
        ) : (
          <>
            <div className="etsy-accounting-file-info">
              <div>
                <FileExcelOutlined style={{ fontSize: 20, color: '#52c41a', marginRight: 8 }} />
                <span className="file-name">{file.name}</span>
              </div>
              <span className="file-size">{formatFileSize(file.size)}</span>
            </div>

            <div className="etsy-accounting-actions">
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleConvert}
                loading={converting}
                className="convert-btn app-btn-primary"
              >
                {converting ? 'Converting...' : 'Convert & Download Excel'}
              </Button>
              <Button
                icon={<DeleteOutlined />}
                onClick={handleClear}
                disabled={converting}
                className="clear-btn"
              >
                Clear
              </Button>
            </div>

            {success && (
              <div className="etsy-accounting-success">
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                <span className="success-text">Excel file generated and downloaded successfully!</span>
              </div>
            )}
          </>
        )}
      </Card>

    </div>
  );
}
